<script setup lang="ts">
import Question from "@/components/general/Question/Question.vue";
import TextField from "@/components/general/TextField/TextField.vue";
import Button from "@/components/general/Button/Button.vue";
import { useBankingStore } from "@/stores";
import { DataLayerInteractionsEnum } from "@/enums/dataLayerInteractions.enum";
import Icon from "@/components/general/Icon/Icon.vue";

const banking = useBankingStore();

const props = defineProps<{
    hasError: {
        accountNumber: boolean;
        sortCode: boolean;
        bankName: boolean;
        branch: boolean;
        postcode: boolean;
    };
    getErrorMessage: (errors: string) => string;
}>();

const emit = defineEmits<{
    (e: "triggerLookup"): void;
    (e: "enterManual"): void;
    (e: "changeDetail", value: string): void;
    (e: "triggerTracking", value: DataLayerInteractionsEnum): void;
    (e: "use-address"): void;
}>();

function cleanUpAccountNumber(e: InputEvent) {
    if (e.inputType === "deleteContentBackward") return;
    banking.accountNumber = banking.accountNumber.replace(/\D/g, "");
}

function cleanUpSortCode(e: InputEvent) {
    if (e.inputType === "deleteContentBackward") return;
    const raw = banking.sortCode.replace(/\D/g, "");
    let formatted = "";

    if (raw.length > 0) formatted += raw.substring(0, 2);
    if (raw.length > 2) formatted += "-" + raw.substring(2, 4);
    if (raw.length > 4) formatted += "-" + raw.substring(4);

    banking.sortCode = formatted;
}
</script>

<template>
    <div id="bankDetails" class="section-card gap-bottom">
        <Question
            id="account-number"
            label="Account number:"
            :error-message="props.getErrorMessage('accountNumber')"
        >
            <TextField
                id="account-number-field"
                v-model="banking.accountNumber"
                type="tel"
                name="account-number"
                placeholder="00000000"
                :maxlength="8"
                :has-error="hasError.accountNumber"
                @input="cleanUpAccountNumber"
                @blur="emit('changeDetail', 'accountNumber')"
            />
        </Question>

        <Question
            id="sort-code-question"
            label="Sort code:"
            :error-message="getErrorMessage('sortCode')"
            help-text="You can find this on your bank statement or on the bottom of your debit card"
            @help-clicked="
                emit(
                    'triggerTracking',
                    DataLayerInteractionsEnum.QuestionPremium
                )
            "
        >
            <template #help>
                <p
                    >You can find this on your bank statement or on the bottom
                    of your debit card</p
                >
                <img
                    class="help-img"
                    src="@img/sortcode-help.jpg"
                    alt="Sort Code"
                />
            </template>

            <TextField
                id="sort-code"
                v-model="banking.sortCode"
                type="tel"
                name="sort-code"
                placeholder="12-34-56"
                :maxlength="8"
                :has-error="hasError.sortCode"
                @input="cleanUpSortCode"
                @blur="emit('changeDetail', 'sortCode')"
            />
        </Question>

        <div
            v-if="banking.validBank !== null && !banking.validBank"
            class="error-tip mt-2"
        >
            Could not find your bank, please enter address manually
        </div>

        <div class="button-layout column gap-top">
            <Button
                id="bankLookup"
                btn-type="primary"
                @click.prevent="$emit('triggerLookup')"
            >
                Locate my bank
            </Button>
            <Button
                id="enter-manual-address"
                btn-type="tertiary"
                @click.prevent="$emit('enterManual')"
            >
                Enter address manually
            </Button>
        </div>
    </div>
    <div v-if="banking.manualAddress">
        <div class="section-card">
            <div class="header-with-button">
                <h4>Enter address</h4>
                <Button
                    id="close-manual-entry"
                    class="close-manual-entry"
                    btn-type="tertiary"
                    tabindex="0"
                    @click.prevent="banking.showManualAddress(false, true)"
                >
                    <Icon name="close" type="png" class="close-icon" />
                </Button>
            </div>
            <p>Please complete the following information</p>
            <Question
                id="bank-name"
                label="Bank:*"
                :error-message="getErrorMessage('bankName')"
            >
                <TextField
                    id="bank-name"
                    v-model="banking.bankName"
                    type="text"
                    name="bank-name"
                    aria-label="Bank name"
                    :maxlength="100"
                    :has-error="hasError.bankName"
                    @blur="emit('changeDetail', 'bankName')"
                    @change="
                        emit(
                            'triggerTracking',
                            DataLayerInteractionsEnum.BankName
                        )
                    "
                />
            </Question>
            <Question
                id="branch"
                label="Branch:*"
                :error-message="getErrorMessage('branch')"
            >
                <TextField
                    id="branch"
                    v-model="banking.branch"
                    type="text"
                    name="branch"
                    aria-label="Bank branch"
                    :maxlength="50"
                    :has-error="hasError.branch"
                    @change="
                        emit(
                            'triggerTracking',
                            DataLayerInteractionsEnum.Branch
                        )
                    "
                />
            </Question>
            <Question id="bank-address-line1" label="Bank Address Line 1:">
                <TextField
                    id="bank-address-line1"
                    v-model="banking.address.line1"
                    type="text"
                    name="bank-address-line1"
                    aria-label="Bank address line 1"
                    :maxlength="50"
                    @change="
                        emit(
                            'triggerTracking',
                            DataLayerInteractionsEnum.BankAddress1
                        )
                    "
                />
            </Question>
            <Question id="bank-address-line2" label="Bank Address Line 2:">
                <TextField
                    id="bank-address-line2"
                    v-model="banking.address.line2"
                    type="text"
                    name="bank-address-line2"
                    aria-label="Bank address line 2"
                    :maxlength="50"
                    @change="
                        emit(
                            'triggerTracking',
                            DataLayerInteractionsEnum.BankAddress2
                        )
                    "
                />
            </Question>
            <Question id="bank-address-line3" label="Bank Address Line 3:">
                <TextField
                    id="bank-address-line3"
                    v-model="banking.address.line3"
                    type="text"
                    name="bank-address-line3"
                    aria-label="Bank address line 3"
                    :maxlength="50"
                    @change="
                        emit(
                            'triggerTracking',
                            DataLayerInteractionsEnum.BankAddress3
                        )
                    "
                />
            </Question>
            <Question
                id="bank-postcode"
                label="Bank Postcode:*"
                :error-message="getErrorMessage('address.postcode')"
            >
                <TextField
                    id="bank-postcode"
                    v-model="banking.address.postcode"
                    type="text"
                    name="bank-postcode"
                    aria-label="Bank postcode"
                    :maxlength="50"
                    :has-error="hasError.postcode"
                    @blur="emit('changeDetail', 'bankPostcode')"
                    @change="
                        emit(
                            'triggerTracking',
                            DataLayerInteractionsEnum.AddressPostcode
                        )
                    "
                />
            </Question>
            <div class="button-layout">
                <Button
                    id="close-bank"
                    btn-type="secondary"
                    tabindex="0"
                    @click="
                        emit(
                            'triggerTracking',
                            DataLayerInteractionsEnum.ButtonCancel
                        );
                        banking.showManualAddress(false, true);
                    "
                >
                    Cancel
                </Button>
                <Button
                    id="apply-bank"
                    btn-type="primary"
                    tabindex="0"
                    @click.prevent="emit('use-address')"
                >
                    Use This Address
                </Button>
            </div>
        </div>
    </div>
</template>

<style lang="css" scoped>
.section-card {
    background: var(--color-background-alt);
    padding: 1rem;
}

.gap {
    margin: 1.5rem 0;
}

.gap-top {
    margin-top: 2rem;
}

.gap-bottom {
    margin-bottom: 2rem;
}

.button-layout {
    display: flex;
    gap: 1rem;
    justify-content: space-between;

    &.column {
        flex-direction: column;
    }
}

.header-with-button {
    display: flex;
    align-items: center;
    justify-content: space-between;

    button {
        max-width: 20px;
        padding: 0;

        &:focus {
            outline: solid 2px var(--signature-blue);
            outline-offset: 0.5rem;
        }
    }

    .close-manual-entry {
        width: 20px;
    }
}

.close-icon {
    width: 20px;
    height: 20px;
}
</style>
